import React, {
  useEffect,
  useState,
  FunctionComponent,
  useCallback,
  KeyboardEvent,
} from 'react';
import { Box, Card, Search } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { useRouter } from 'next/router';
import { askingAQuestion } from '@wix/bi-logger-new-help-center/v2';
import { useFedops, useHttpClient } from '@wix/fe-essentials-standalone';
import { v4 as uuidv4 } from 'uuid';
import { DATA_HOOKS } from '../../dataHooks';
import { FEDOPS, PAGES } from '../../constants';
import {
  ArticleSearchResult,
  ArticleSearchResultItem,
  PageType,
} from '../../types';
import { SearchBarResults } from './SearchBarResults';
import { EmptySearchResults } from './EmptySearchResults';
import { useBI } from '../../hooks/useBI';
import {
  mapArticleTypeToItemType,
  mapPageTypeToSourceName,
} from '../../utils/bi';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import css from './index.module.scss';
import { pushUrl } from '../InnerLink';

type SearchBarProps = {
  pageType: PageType;
  itemId?: string;
  itemType?: string;
};

export const SearchBar: FunctionComponent<SearchBarProps> = ({
  pageType,
  itemId,
  itemType,
}: SearchBarProps) => {
  const { t } = useTranslation();
  const { locale, asPath } = useRouter();
  const httpClient = useHttpClient();
  const [hideResults, setHideResults] = useState(false);
  const { sendBIEvent } = useBI();
  const fedopsApi = useFedops();
  const ref = useOutsideClick(() => {
    setHideResults(true);
  });

  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchSessionId, setSearchSessionId] = useState(uuidv4());
  const [articles, setArticles] = useState<ArticleSearchResultItem[]>([]);
  const [focusedArticleIndex, setFocusedArticleIndex] = useState<number>(-1);

  const search = useCallback(async (): Promise<void> => {
    if (inputText?.length > 0) {
      setIsLoading(true);
      fedopsApi.interactionStarted(FEDOPS.INTERACTION_NAMES.SEARCH_ARTICLES);
      const { data } = await httpClient.get<ArticleSearchResult>(
        '/api/article/search',
        {
          params: {
            locale,
            text: inputText,
          },
        }
      );
      fedopsApi.interactionEnded(FEDOPS.INTERACTION_NAMES.SEARCH_ARTICLES);

      setIsLoading(false);
      const newArticles = data.items.slice(0, 5);
      setArticles(newArticles);
      setFocusedArticleIndex(-1);
      await sendBIEvent(
        askingAQuestion({
          source_name: mapPageTypeToSourceName(pageType),
          item_id: itemId,
          item_type: itemType,
          kb_lang: locale as string,
          presented_content: JSON.stringify(
            newArticles.map((article, index) => ({
              order: index + 1,
              item_id: article.id,
              locale,
              type: mapArticleTypeToItemType(article.type),
            }))
          ),
          question: inputText,
          search_session_Id: searchSessionId,
          search_method: data.searchMethod,
          search_version: data.searchVersion,
        })
      );
    } else {
      setArticles([]);
    }
  }, [inputText]);

  const onClear = () => {
    setInputText('');
    setArticles([]);
    setFocusedArticleIndex(-1);
    setSearchSessionId(uuidv4());
  };

  useEffect(() => {
    onClear();
  }, [asPath]);

  useEffect(() => {
    void (async () => {
      await search();
    })();
  }, [inputText, search]);

  const handleKeyboardEvent = useCallback(
    async (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
          setFocusedArticleIndex(
            focusedArticleIndex === articles.length
              ? focusedArticleIndex
              : focusedArticleIndex + 1
          );
          break;
        case 'ArrowUp':
          setFocusedArticleIndex(
            focusedArticleIndex > -1
              ? focusedArticleIndex - 1
              : focusedArticleIndex
          );
          break;
        case 'Enter':
          if (
            focusedArticleIndex >= 0 &&
            focusedArticleIndex <= articles.length - 1
          ) {
            await pushUrl(articles[focusedArticleIndex].uri);
          } else if (focusedArticleIndex === articles.length) {
            await pushUrl(`/search?term=${inputText}`);
          }
          break;
      }
    },
    [articles, focusedArticleIndex, inputText]
  );

  return (
    <div
      ref={ref}
      className={css.wrapper}
      data-hook={DATA_HOOKS.SEARCH_BAR}
      onKeyUp={handleKeyboardEvent}
    >
      <Box
        direction="vertical"
        verticalAlign="middle"
        className={
          pageType === PAGES.HOMEPAGE
            ? css.searchBarWrapperHomepage
            : css.searchBarWrapperSubHeader
        }
      >
        <Search
          debounceMs={1000}
          maxLength={100}
          placeholder={t('searchbar.placeholder')}
          onChange={(e) => {
            setInputText(e.target.value);
            setFocusedArticleIndex(-1);
          }}
          onClear={onClear}
          onManuallyInput={async (val) => {
            if (focusedArticleIndex === -1) {
              setInputText(val);
            }
          }}
          value={inputText}
          className={css.searchBar}
          dataHook={DATA_HOOKS.SEARCH_BAR_INPUT}
          onFocus={() => {
            setHideResults(false);
            setFocusedArticleIndex(-1);
          }}
        />
        {inputText !== '' && !hideResults && (
          <Card
            showShadow
            className={
              pageType === PAGES.HOMEPAGE
                ? css.searchBarResultsWrapperHomepage
                : css.searchBarResultsWrapperSubHeader
            }
          >
            {articles.length > 0 && (
              <SearchBarResults
                articles={articles}
                inputText={inputText}
                itemId={itemId}
                itemType={itemType}
                searchSessionId={searchSessionId}
                pageType={pageType}
                onClear={onClear}
                focusedArticleIndex={focusedArticleIndex}
                setFocusedArticleIndex={setFocusedArticleIndex}
              />
            )}
            {articles.length === 0 && !isLoading && <EmptySearchResults />}
          </Card>
        )}
      </Box>
    </div>
  );
};
